import { useMemo, useRef, useState } from 'react'
import type { GameSide } from '../chatv1/transport'
import { SIZE, STAR_POINTS, allPoints, columnLetter, parsePoint, stonesOf } from './gomokuGeom'

/**
 * THE GOMOKU BOARD — owner, 2026-10-19: 西洋棋做完之後 … 之後再做五子棋.
 *
 * Fifteen lines each way on wood, the stones on the crossings; the last stone carries a red dot, the five that won
 * are ringed, and your agent's hint is a faint stone of your colour. Every crossing is a button named by its point
 * ("h8, empty"), so the board is played with a mouse, a finger or the keyboard.
 *
 * ON A PHONE A STONE TAKES TWO TAPS. A crossing on a phone is a few millimetres wide and a stone cannot be taken back,
 * so the first tap shows your stone there and the second, on the same point, places it (another point moves it). A
 * mouse places with one click and shows the stone under the pointer before it.
 *
 * The board is the same for both players — row 1 at the bottom — so a point named in the chat is where both see it.
 */

/** Board units: crossings at 0…14, a margin of one for the coordinates. */
const PAD = 1
const VIEW = SIZE - 1 + PAD * 2

export function GomokuBoard({
  moves,
  mine,
  movable,
  last,
  line,
  hint,
  onPlace,
}: {
  moves: readonly string[]
  /** The viewer's colour, for the stone they are about to place (and the hint's); null for an onlooker. */
  mine: GameSide | null
  /** The viewer may place now. */
  movable: boolean
  last: string | null
  /** The five (or more) that won. */
  line: readonly string[] | null
  /** Your agent's suggested point. */
  hint: string | null
  onPlace: (point: string) => void
}) {
  const stones = useMemo(() => stonesOf(moves), [moves])
  const points = useMemo(allPoints, [])
  const won = useMemo(() => new Set(line ?? []), [line])
  // A touch's first tap (shown, not placed), and the point under a mouse.
  const [preview, setPreview] = useState<string | null>(null)
  const [hover, setHover] = useState<string | null>(null)
  const pointer = useRef<string | null>(null)

  // A new stone on the board (either side's) clears a half-made choice.
  const [seen, setSeen] = useState(moves.length)
  if (seen !== moves.length) {
    setSeen(moves.length)
    setPreview(null)
    setHover(null)
  }

  const pick = (p: string) => {
    const how = pointer.current
    pointer.current = null
    if (!movable || stones.has(p)) {
      setPreview(null)
      return
    }
    // A finger or a pen: show first, place on the second tap of the same point. A mouse or a key: place now.
    if ((how === 'touch' || how === 'pen') && preview !== p) {
      setPreview(p)
      return
    }
    setPreview(null)
    onPlace(p)
  }

  const at = (p: string): [number, number] => {
    const [c, r] = parsePoint(p)!
    return [c + PAD, SIZE - 1 - r + PAD]
  }
  const ghost = movable && mine ? (preview ?? hover) : null
  const hitInset = `${((PAD - 0.5) / VIEW) * 100}%`

  return (
    <div className="gb-wrap">
      <div className={`gb-board${movable ? ' gb-live' : ''}`} onPointerLeave={() => setHover(null)}>
        <svg className="gb-svg" viewBox={`0 0 ${VIEW} ${VIEW}`} aria-hidden="true">
          <defs>
            <radialGradient id="gb-black" cx="35%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#6a6a6a" />
              <stop offset="45%" stopColor="#262626" />
              <stop offset="100%" stopColor="#050505" />
            </radialGradient>
            <radialGradient id="gb-white" cx="35%" cy="30%" r="75%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="70%" stopColor="#ecebe6" />
              <stop offset="100%" stopColor="#bdbab2" />
            </radialGradient>
            {/* The wood: fine grain, and a soft light from the top left — under the lines and the stones. */}
            <pattern id="gb-grain" width="0.42" height={VIEW} patternUnits="userSpaceOnUse" patternTransform="rotate(7)">
              <rect width="0.1" height={VIEW} fill="rgba(120, 72, 24, 0.07)" />
            </pattern>
            <radialGradient id="gb-light" cx="25%" cy="15%" r="60%">
              <stop offset="0%" stopColor="rgba(255, 236, 196, 0.45)" />
              <stop offset="100%" stopColor="rgba(255, 236, 196, 0)" />
            </radialGradient>
          </defs>
          <rect className="gb-wood" x="0" y="0" width={VIEW} height={VIEW} />
          <rect x="0" y="0" width={VIEW} height={VIEW} fill="url(#gb-grain)" />
          <rect x="0" y="0" width={VIEW} height={VIEW} fill="url(#gb-light)" />
          {Array.from({ length: SIZE }, (_, i) => (
            <g key={i} className="gb-line">
              <line x1={PAD} y1={PAD + i} x2={PAD + SIZE - 1} y2={PAD + i} />
              <line x1={PAD + i} y1={PAD} x2={PAD + i} y2={PAD + SIZE - 1} />
            </g>
          ))}
          {STAR_POINTS.map((p) => {
            const [x, y] = at(p)
            return <circle key={p} className="gb-star" cx={x} cy={y} r={0.11} />
          })}
          {Array.from({ length: SIZE }, (_, i) => (
            <g key={`c${i}`} className="gb-coord">
              <text x={PAD + i} y={VIEW - 0.28}>
                {columnLetter(i)}
              </text>
              <text x={0.42} y={PAD + (SIZE - 1 - i) + 0.12}>
                {i + 1}
              </text>
            </g>
          ))}
          {[...stones].map(([p, s]) => {
            const [x, y] = at(p)
            return (
              <g key={p}>
                <circle className="gb-shadow" cx={x + 0.05} cy={y + 0.07} r={0.45} />
                <circle cx={x} cy={y} r={0.45} fill={`url(#gb-${s.side === 'b' ? 'black' : 'white'})`} className={`gb-stone gb-${s.side}`} />
                {won.has(p) && <circle className="gb-won" cx={x} cy={y} r={0.36} />}
                {p === last && <circle className="gb-last" cx={x} cy={y} r={0.1} />}
              </g>
            )
          })}
          {hint && !stones.has(hint) && mine && (
            <g className="gb-hint">
              <circle cx={at(hint)[0]} cy={at(hint)[1]} r={0.45} fill={`url(#gb-${mine === 'b' ? 'black' : 'white'})`} opacity={0.45} />
              <circle className="gb-hint-ring" cx={at(hint)[0]} cy={at(hint)[1]} r={0.5} />
            </g>
          )}
          {ghost && !stones.has(ghost) && (
            <circle
              className="gb-ghost"
              cx={at(ghost)[0]}
              cy={at(ghost)[1]}
              r={0.45}
              fill={`url(#gb-${mine === 'b' ? 'black' : 'white'})`}
              opacity={preview ? 0.75 : 0.45}
            />
          )}
        </svg>
        <div className="gb-hits" style={{ inset: hitInset }}>
          {points.map((p) => {
            const s = stones.get(p)
            return (
              <button
                key={p}
                type="button"
                className="gb-pt"
                aria-label={`${p}, ${s ? `${s.side === 'b' ? 'black' : 'white'} stone` : 'empty'}`}
                aria-pressed={preview === p ? true : undefined}
                // Not `disabled`: a disabled button swallows the pointer, and the stone under a mouse must follow it.
                aria-disabled={!movable || !!s}
                onPointerDown={(e) => {
                  pointer.current = e.pointerType
                }}
                onPointerEnter={(e) => {
                  if (e.pointerType === 'mouse') setHover(p)
                }}
                onClick={() => pick(p)}
              />
            )
          })}
        </div>
      </div>
      {preview && (
        <p className="gb-say" role="status">
          Tap {preview} again to place your stone
        </p>
      )}
    </div>
  )
}
